import { useEffect, useState } from "react";
import client from "@/api/client";
import { PageHeader, Section, StatusPill } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { DownloadSimple, FileXls } from "@phosphor-icons/react";

const REPORTS = [
  { key: "salesman",             label: "Salesman-wise",      endpoint: "/reports/salesman",          cols: ["salesman","orders","quantity"] },
  { key: "employee",             label: "Employee Perf.",     endpoint: "/reports/employee-performance", cols: ["mobile","name","role","orders_entered","picked","scanned","packed","dispatched"] },
  { key: "dispatch",             label: "Dispatch",           endpoint: "/reports/dispatch",          cols: ["order_no","company_name","retailer_name","salesman","order_quantity","stage"] },
  { key: "pending",              label: "Pending",            endpoint: "/reports/pending",           cols: ["order_no","retailer_name","salesman","stage","order_datetime"] },
  { key: "delayed",              label: "Delayed (>24h)",     endpoint: "/reports/delayed",           cols: ["order_no","retailer_name","stage","order_datetime"] },
  { key: "party",                label: "Party-wise",         endpoint: "/reports/party-wise",        cols: ["retailer","orders","quantity"] },
  { key: "daily",                label: "Daily Dispatch",     endpoint: "/reports/daily-dispatch",    cols: ["date","dispatched","quantity"] },
  { key: "monthly",              label: "Monthly Dispatch",   endpoint: "/reports/monthly-dispatch",  cols: ["month","dispatched","quantity"] },
  { key: "avg",                  label: "Avg Processing (hrs)", endpoint: "/reports/avg-processing-time" },
  { key: "completion",           label: "Order Completion",   endpoint: "/reports/order-completion",  cols: ["order_no","retailer_name","order_datetime","completion_hours"] },
];

function toCSV(rows, cols) {
  const header = cols.join(",");
  const body = rows.map(r => cols.map(c => {
    const v = r[c] ?? "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  }).join(",")).join("\n");
  return header + "\n" + body;
}

export default function Reports() {
  const [tab, setTab] = useState("salesman");
  const [data, setData] = useState({});

  useEffect(() => {
    const r = REPORTS.find(x=>x.key===tab);
    if (!data[tab]) {
      client.get(r.endpoint).then(({ data }) => setData((d)=>({ ...d, [tab]: data })));
    }
  }, [tab]); // eslint-disable-line

  const current = REPORTS.find(x=>x.key===tab);
  const rows = data[tab];

  const downloadCsv = () => {
    if (!Array.isArray(rows) || !current.cols) return;
    const blob = new Blob([toCSV(rows, current.cols)], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `${current.key}.csv`);
  };

  const downloadXlsx = () => {
    if (!Array.isArray(rows) || !current.cols) return;
    const ws = XLSX.utils.json_to_sheet(rows.map(r => Object.fromEntries(current.cols.map(c => [c, r[c] ?? ""]))));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, current.label.slice(0, 30));
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), `${current.key}.xlsx`);
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Operational analytics"
        actions={current.cols && (
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-sm" data-testid="report-csv-btn" onClick={downloadCsv}><DownloadSimple size={16} className="mr-1"/> CSV</Button>
            <Button variant="outline" className="rounded-sm" data-testid="report-xlsx-btn" onClick={downloadXlsx}><FileXls size={16} className="mr-1"/> Excel</Button>
          </div>
        )}
      />
      <Section>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex flex-wrap h-auto justify-start gap-1 bg-slate-100 p-1 rounded-sm">
            {REPORTS.map(r=>(
              <TabsTrigger key={r.key} value={r.key} data-testid={`tab-${r.key}`} className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm text-xs">
                {r.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {REPORTS.map(r=>(
            <TabsContent key={r.key} value={r.key} className="mt-4">
              {r.key === "avg" ? (
                <AvgTable data={data[r.key]}/>
              ) : (
                <GenericTable rows={data[r.key]} cols={r.cols}/>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </Section>
    </div>
  );
}

function GenericTable({ rows, cols }) {
  if (!Array.isArray(rows)) return <div className="text-slate-500 p-6">Loading…</div>;
  if (rows.length === 0) return <div className="bg-white border border-slate-200 rounded-sm p-10 text-center text-slate-500">No data</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
      <table className="w-full text-sm" data-testid="report-table">
        <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
          {cols.map(c=><th key={c} className="px-4 py-3">{c.replace(/_/g," ")}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r,i)=>(
            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
              {cols.map(c=>(
                <td key={c} className="px-4 py-2">
                  {c === "stage" ? <StatusPill status={r[c]}/> :
                   c === "order_datetime" || c === "dispatched_at" ? (r[c] ? new Date(r[c]).toLocaleString() : "") :
                   typeof r[c] === "number" ? <span className="font-mono">{r[c]}</span> :
                   String(r[c] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AvgTable({ data }) {
  if (!data) return <div className="text-slate-500 p-6">Loading…</div>;
  const rows = [
    { name: "Order → Picking", hours: data.order_to_pick, color: "bg-emerald-50 border-emerald-200" },
    { name: "Picking → Scanning", hours: data.pick_to_scan, color: "bg-amber-50 border-amber-200" },
    { name: "Scanning → Packing", hours: data.scan_to_pack, color: "bg-blue-50 border-blue-200" },
    { name: "Packing → Dispatch", hours: data.pack_to_dispatch, color: "bg-violet-50 border-violet-200" },
    { name: "Total (Order → Dispatch)", hours: data.total, color: "bg-slate-100 border-slate-300" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {rows.map(r=>(
        <div key={r.name} className={`border rounded-sm p-5 ${r.color}`}>
          <div className="label-cap">{r.name}</div>
          <div className="font-display text-4xl font-black mt-2">{r.hours}<span className="text-lg font-semibold text-slate-500 ml-1">hrs</span></div>
        </div>
      ))}
    </div>
  );
}
